import React, { useCallback } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SidePanelHandle } from 'shared/components/side_panel';
import { withLocalePrefix } from 'shared/utils/translation';
import { useMyGreenhouseCandidate } from 'talent_matching/my_greenhouse/hooks/use_my_greenhouse_candidates';
import { useMyGreenhouseTabContext } from 'talent_matching/contexts/my_greenhouse_tab_context';
import { useUserContext } from 'talent_matching/contexts/user_context';
import candidateDisplayName from 'talent_matching/components/shared/candidate_display_name';
import BaseSidePanel, {
  CandidateName,
  SidePanelNavigationProps,
} from './base_side_panel';
import MyGreenhouseSidePanelBody from './my_greenhouse_side_panel_body';

const t = withLocalePrefix('talent_matching');

type Props = SidePanelNavigationProps;

const MyGreenhouseSidePanel = React.forwardRef<SidePanelHandle, Props>(
  (
    {
      onClose,
      applicationId,
      onToggle,
      isVisible,
      rowCount,
      currentIndex,
      previousCandidate,
      nextCandidate,
      selectCandidate,
    },
    ref
  ) => {
    const { candidate } = useMyGreenhouseCandidate(applicationId);
    const { hiringPlanId, userId } = useUserContext();
    const { isForager } = useMyGreenhouseTabContext();

    const application = candidate
      ? {
          id: candidate.id,
          hiringPlanId,
          fullName: candidate.fullName,
        }
      : null;

    const [activeSidePanelTab, setActiveSidePanelTab] =
      React.useState<string>('work_preferences');

    const handlePanelRef = useCallback(
      (node: SidePanelHandle | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    const tabsConfig = [
      { id: 'work_preferences', label: t('filter_row.work_preferences.title') },
      { id: 'resume', label: t('panel.resume.title') },
      ...(isForager
        ? [{ id: 'match_report', label: t('panel.match_report.title') }]
        : []),
    ];

    const tabIds = tabsConfig.map((tab) => tab.id);
    const selectedTabIndex = Math.max(
      tabIds.indexOf(activeSidePanelTab ?? 'work_preferences'),
      0
    );

    const handleTabChange = (index: number) => {
      const tabId = tabIds[index];
      if (tabId) {
        setActiveSidePanelTab(tabId);
      }
    };

    return (
      <BaseSidePanel
        handlePanelRef={handlePanelRef}
        application={application}
        isLoading={!candidate}
        onClose={onClose}
        onToggle={onToggle}
        isVisible={isVisible}
        rowCount={rowCount}
        currentIndex={currentIndex}
        previousCandidate={previousCandidate}
        nextCandidate={nextCandidate}
        selectCandidate={selectCandidate}
        applicationId={applicationId}
        activeSidePanelTab={activeSidePanelTab}
        setActiveSidePanelTab={setActiveSidePanelTab}
        renderHeader={({ application: app }) => (
          <Stack spacing={1} sx={{ width: '100%' }}>
            <CandidateName large>
              <span className="pii-blur" data-amp-mask>
                {candidateDisplayName(app.fullName)}
              </span>
            </CandidateName>
          </Stack>
        )}
      >
        {(renderProps) => (
          <MyGreenhouseSidePanelBody
            {...renderProps}
            candidate={candidate}
            hiringPlanId={hiringPlanId}
            userId={userId}
            tabsConfig={tabsConfig}
            selectedTabIndex={selectedTabIndex}
            onTabChange={handleTabChange}
          />
        )}
      </BaseSidePanel>
    );
  }
);

MyGreenhouseSidePanel.displayName = 'MyGreenhouseSidePanel';

export default MyGreenhouseSidePanel;
